import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ShieldAlert, CheckCircle2, XCircle, MinusCircle, ClipboardCheck } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { axiosInstance } from '@/App';

const STATUS_CLASS = {
  pending: 'bg-yellow-500',
  verified: 'bg-red-600',
  not_applicable: 'bg-gray-500',
  dismissed: 'bg-gray-400',
};

const SEVERITY_CLASS = {
  Critical: 'bg-red-700',
  High: 'bg-red-500',
  Medium: 'bg-orange-500',
  Low: 'bg-green-600',
};

/**
 * AI-identified hazards the auditor may not have evaluated. These are explicitly NOT findings:
 * nothing here changes the compliance rate or the official risk classification until a supervisor
 * verifies it.
 */
export const PotentialHazards = ({ auditId, hazards = [], onUpdated, canReview = true }) => {
  const { t } = useTranslation();
  const [comments, setComments] = useState({});
  const [saving, setSaving] = useState(null);

  if (!hazards.length) return null;

  const open = hazards.filter((h) => (h.reviewStatus || 'pending') === 'pending');
  const imminent = open.filter((h) => h.imminentDanger);
  const statusLabel = (status) => t(`potentialHazards.status.${status}`);

  const review = async (hazard, reviewStatus) => {
    setSaving(`${hazard.id}-${reviewStatus}`);
    try {
      const res = await axiosInstance.put(
        `/safety-audits/${auditId}/potential-hazards/${hazard.id}`,
        { reviewStatus, reviewComment: comments[hazard.id] || '' }
      );
      toast.success(
        reviewStatus === 'verified'
          ? t('potentialHazards.verifiedToast')
          : t('potentialHazards.markedToast', { status: statusLabel(reviewStatus).toLowerCase() })
      );
      onUpdated?.(res.data.hazard);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('potentialHazards.saveError'));
    } finally {
      setSaving(null);
    }
  };

  const createCorrectiveAction = async (hazard) => {
    setSaving(`${hazard.id}-action`);
    try {
      await axiosInstance.post('/company/corrective-actions/from-hazard', {
        auditId,
        hazardId: hazard.id,
      });
      toast.success(t('correctiveWorkflow.createdFromHazard'));
      onUpdated?.({ ...hazard, correctiveActionId: 'pending-refresh' });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('correctiveWorkflow.createFromHazardFailed'));
    } finally {
      setSaving(null);
    }
  };

  return (
    <Card className="border-amber-300" data-testid="potential-hazards-panel">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-600" />
              {t('potentialHazards.title')}
            </h3>
            <p className="text-xs text-gray-600 mt-1 max-w-3xl">
              {t('potentialHazards.introLead')}{' '}
              <strong>{t('potentialHazards.introEmphasis')}</strong>{' '}
              {t('potentialHazards.introTail')}
            </p>
          </div>
          <Badge className="bg-amber-500 whitespace-nowrap" data-testid="potential-hazards-open-count">
            {t('potentialHazards.awaitingReview', { n: open.length })}
          </Badge>
        </div>

        {imminent.length > 0 && (
          <div
            className="mb-4 rounded-md border border-red-300 bg-red-50 p-3"
            data-testid="imminent-danger-alert"
          >
            <p className="flex items-center gap-2 font-semibold text-red-800">
              <AlertTriangle className="w-5 h-5" />
              {t('potentialHazards.imminentTitle')}
            </p>
            <p className="text-xs text-red-800 mt-1">
              {t('potentialHazards.imminentBody', { n: imminent.length })}
            </p>
          </div>
        )}

        <div className="space-y-4">
          {hazards.map((hazard) => {
            const status = hazard.reviewStatus || 'pending';
            return (
              <div
                key={hazard.id}
                className="rounded-md border border-gray-200 p-3"
                data-testid={`potential-hazard-${hazard.id}`}
              >
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="font-medium text-sm">{hazard.hazard}</span>
                  <Badge className={SEVERITY_CLASS[hazard.potentialSeverity] || 'bg-orange-500'}>
                    {t('potentialHazards.severityLabel', {
                      severity: t(`potentialHazards.severity.${hazard.potentialSeverity}`, {
                        defaultValue: hazard.potentialSeverity,
                      }),
                    })}
                  </Badge>
                  <Badge
                    className={STATUS_CLASS[status] || STATUS_CLASS.pending}
                    data-testid={`potential-hazard-status-${hazard.id}`}
                  >
                    {statusLabel(status)}
                  </Badge>
                  {hazard.imminentDanger && (
                    <Badge className="bg-red-700">{t('potentialHazards.imminentBadge')}</Badge>
                  )}
                </div>

                {hazard.description && (
                  <p className="text-sm text-gray-700 mb-2">{hazard.description}</p>
                )}

                <dl className="text-xs text-gray-700 space-y-1">
                  <div>
                    <dt className="inline font-semibold">{t('potentialHazards.triggeredBy')} </dt>
                    <dd className="inline">{hazard.triggeredBy}</dd>
                  </div>
                  {hazard.possibleConsequences && (
                    <div>
                      <dt className="inline font-semibold">
                        {t('potentialHazards.possibleConsequences')}{' '}
                      </dt>
                      <dd className="inline">{hazard.possibleConsequences}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="inline font-semibold">{t('potentialHazards.oshaReference')} </dt>
                    <dd className="inline">
                      {hazard.oshaReference || t('potentialHazards.noOsha')}
                    </dd>
                  </div>
                </dl>

                {hazard.verificationSteps?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs font-semibold text-gray-700">
                      {t('potentialHazards.verificationSteps')}
                    </p>
                    <ul className="list-disc list-inside text-xs text-gray-700">
                      {hazard.verificationSteps.map((step, idx) => <li key={idx}>{step}</li>)}
                    </ul>
                  </div>
                )}

                {hazard.preventiveMeasures?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs font-semibold text-gray-700">
                      {t('potentialHazards.preventiveMeasures')}
                    </p>
                    <ul className="list-disc list-inside text-xs text-gray-700">
                      {hazard.preventiveMeasures.map((measure, idx) => <li key={idx}>{measure}</li>)}
                    </ul>
                  </div>
                )}

                {status !== 'pending' && (
                  <p className="text-xs text-gray-500 mt-2 italic">
                    {statusLabel(status)}
                    {hazard.reviewedByName
                      ? ` ${t('potentialHazards.reviewedBy', { name: hazard.reviewedByName })}`
                      : ''}
                    {hazard.reviewedAt
                      ? ` ${t('potentialHazards.reviewedOn', { date: hazard.reviewedAt.slice(0, 10) })}`
                      : ''}
                    {hazard.reviewComment ? ` — "${hazard.reviewComment}"` : ''}
                  </p>
                )}

                {status === 'verified' && !hazard.correctiveActionId && canReview && (
                  <div className="mt-3">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={saving !== null}
                      onClick={() => createCorrectiveAction(hazard)}
                      data-testid={`potential-hazard-create-action-${hazard.id}`}
                    >
                      <ClipboardCheck className="w-4 h-4 mr-1" />
                      {t('correctiveWorkflow.createFromHazard')}
                    </Button>
                  </div>
                )}

                {hazard.correctiveActionId && (
                  <p className="text-xs text-green-700 mt-2" data-testid={`potential-hazard-action-linked-${hazard.id}`}>
                    {t('correctiveWorkflow.createdFromHazard')}
                  </p>
                )}

                {canReview && !hazard.correctiveActionId && (
                  <div className="mt-3 space-y-2">
                    <Textarea
                      rows={2}
                      placeholder={t('potentialHazards.commentPlaceholder')}
                      value={comments[hazard.id] ?? hazard.reviewComment ?? ''}
                      onChange={(e) => setComments({ ...comments, [hazard.id]: e.target.value })}
                      data-testid={`potential-hazard-comment-${hazard.id}`}
                    />
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        className="bg-red-600 hover:bg-red-700"
                        disabled={saving !== null}
                        onClick={() => review(hazard, 'verified')}
                        data-testid={`potential-hazard-verify-${hazard.id}`}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        {t('potentialHazards.verifyButton')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={saving !== null}
                        onClick={() => review(hazard, 'not_applicable')}
                        data-testid={`potential-hazard-na-${hazard.id}`}
                      >
                        <MinusCircle className="w-4 h-4 mr-1" />
                        {t('potentialHazards.notApplicableButton')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={saving !== null}
                        onClick={() => review(hazard, 'dismissed')}
                        data-testid={`potential-hazard-dismiss-${hazard.id}`}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        {t('potentialHazards.dismissButton')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default PotentialHazards;
